/**
 * Notification Queue Manager
 * Handles queue operations, deduplication, and priority management
 */

import { 
  NotificationObject, 
  AnimationState,
  QueueConfig 
} from '../types';
import { 
  areNotificationsDuplicate, 
  sortNotificationsByPriority,
  getNotificationKey 
} from './notification-factory';

/**
 * Default queue configuration
 */
const DEFAULT_QUEUE_CONFIG: QueueConfig = {
  maxSize: 100,
  deduplicationKey: getNotificationKey,
  priorityComparator: sortNotificationsByPriority,
};

/**
 * Queue Manager Class
 */
export class NotificationQueueManager {
  private queue: NotificationObject[] = [];
  private config: QueueConfig;
  
  constructor(config?: Partial<QueueConfig>) {
    this.config = { ...DEFAULT_QUEUE_CONFIG, ...config };
  }

  /**
   * Add a notification to the queue
   */
  add(notification: NotificationObject): NotificationObject[] {
    // Check for duplicates if preventDuplicate is enabled
    if (notification.preventDuplicate) {
      const duplicate = this.queue.find(existing => 
        areNotificationsDuplicate(existing, notification)
      );
      
      if (duplicate) {
        // Update the existing notification's timestamp to bump it up
        duplicate.timestamp = notification.timestamp;
        return this.getQueue();
      }
    }

    // Add to queue
    this.queue.push(notification);

    // Enforce max size
    if (this.config.maxSize && this.queue.length > this.config.maxSize) {
      // Remove oldest notifications that are not persistent
      const removableNotifications = this.queue
        .filter(n => n.duration !== undefined)
        .sort((a, b) => a.timestamp - b.timestamp);
      
      if (removableNotifications.length > 0) {
        const toRemove = removableNotifications[0];
        this.remove(toRemove.id);
      }
    }

    // Sort by priority
    this.sort();

    return this.getQueue();
  }

  /**
   * Remove a notification from the queue
   */
  remove(id: string): NotificationObject[] {
    this.queue = this.queue.filter(n => n.id !== id);
    return this.getQueue();
  }

  /**
   * Update a notification in the queue
   */
  update(id: string, updates: Partial<NotificationObject>): NotificationObject[] {
    const index = this.queue.findIndex(n => n.id === id);
    
    if (index !== -1) {
      this.queue[index] = {
        ...this.queue[index],
        ...updates,
        id: this.queue[index].id, // Preserve ID
        timestamp: this.queue[index].timestamp, // Preserve original timestamp
      };
      
      // Re-sort if priority changed
      if (updates.priority !== undefined) {
        this.sort();
      }
    }

    return this.getQueue();
  }

  /**
   * Update animation state for a notification
   */
  updateAnimationState(id: string, state: AnimationState): NotificationObject[] {
    return this.update(id, { animationState: state });
  }

  /**
   * Clear all notifications
   */
  clear(): NotificationObject[] {
    this.queue = [];
    return this.getQueue();
  }

  /**
   * Clear all notifications of a specific kind
   */
  clearByKind(kind: string): NotificationObject[] {
    this.queue = this.queue.filter(n => n.kind !== kind);
    return this.getQueue();
  }

  /**
   * Clear all notifications of a specific group
   */
  clearByGroup(groupId: string): NotificationObject[] {
    this.queue = this.queue.filter(n => n.groupId !== groupId);
    return this.getQueue();
  }

  /**
   * Get notifications that should be visible
   */
  getVisible(maxVisible: number): NotificationObject[] {
    return this.queue
      .filter(n => n.animationState !== 'exited')
      .slice(0, maxVisible);
  }

  /**
   * Get notifications that are queued (not visible)
   */
  getQueued(maxVisible: number): NotificationObject[] {
    return this.queue
      .filter(n => n.animationState !== 'exited')
      .slice(maxVisible);
  }

  /**
   * Get all notifications
   */
  getQueue(): NotificationObject[] {
    return [...this.queue];
  }

  /**
   * Get notification by ID
   */
  getById(id: string): NotificationObject | undefined {
    return this.queue.find(n => n.id === id);
  }

  /**
   * Get notifications by group
   */
  getByGroup(groupId: string): NotificationObject[] {
    return this.queue.filter(n => n.groupId === groupId);
  }

  /**
   * Check if queue has a specific notification
   */
  has(id: string): boolean {
    return this.queue.some(n => n.id === id);
  }

  /**
   * Get queue size
   */
  size(): number {
    return this.queue.length;
  }

  /**
   * Check if queue is empty
   */
  isEmpty(): boolean {
    return this.queue.length === 0;
  }

  /**
   * Check if queue is full
   */
  isFull(): boolean {
    return this.config.maxSize ? this.queue.length >= this.config.maxSize : false;
  }

  /**
   * Sort the queue by priority
   */
  private sort(): void {
    if (this.config.priorityComparator) {
      this.queue.sort(this.config.priorityComparator);
    }
  }

  /**
   * Process expired notifications
   */
  processExpired(currentTime: number): string[] {
    const expiredIds: string[] = [];
    
    this.queue.forEach(notification => {
      if (notification.duration && notification.animationState === 'idle') {
        const elapsed = currentTime - notification.timestamp;
        if (elapsed >= notification.duration) {
          expiredIds.push(notification.id);
        }
      }
    });

    return expiredIds;
  }

  /**
   * Group notifications by groupId
   */
  getGroupedNotifications(): Map<string | undefined, NotificationObject[]> {
    const grouped = new Map<string | undefined, NotificationObject[]>();
    
    this.queue.forEach(notification => {
      const key = notification.groupId;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(notification);
    });

    return grouped;
  }

  /**
   * Get statistics about the queue
   */
  getStats(): {
    total: number;
    byKind: Record<string, number>;
    byType: Record<string, number>;
    persistent: number;
    temporary: number;
  } {
    const stats = {
      total: this.queue.length,
      byKind: {} as Record<string, number>,
      byType: {} as Record<string, number>,
      persistent: 0,
      temporary: 0,
    };

    this.queue.forEach(notification => {
      // Count by kind
      stats.byKind[notification.kind] = (stats.byKind[notification.kind] || 0) + 1;
      
      // Count by type
      stats.byType[notification.type] = (stats.byType[notification.type] || 0) + 1;
      
      // Count persistent vs temporary
      if (notification.duration === undefined) {
        stats.persistent++;
      } else {
        stats.temporary++;
      }
    });

    return stats;
  }
}

/**
 * Create a new queue manager instance
 */
export const createQueueManager = (config?: Partial<QueueConfig>): NotificationQueueManager => {
  return new NotificationQueueManager(config);
};
